import { z } from 'zod';

import { defineRoute } from '../http/route-contract';
import { IsoDateTimeSchema } from '../primitives/datetime';
import { PositiveDecimalSchema } from '../primitives/decimal';
import { ChannelIdSchema, PositionIdSchema } from '../primitives/ids';
import { MoneySchema } from '../primitives/money';
import { openEnum, openReasonCode } from '../primitives/open-enum';
import { ClosingReasonSchema, ReasonCodeSchema } from '../reason-code';

/** Buy or sell. */
export const TradeSideSchema = z.enum(['BUY', 'SELL']);

/**
 * E8 states: opening, open, partial, closing (E10), lost track of (FXM-04 §5, technical spec §5).
 * Closed on purpose, unlike the other response enums: what E8 shows for a status it does not know
 * is not decided (open question for C1, spec §9 13/10/2026).
 */
export const OpenPositionStatusSchema = z.enum([
  'OPENING',
  'OPEN',
  'PARTIALLY_CLOSED',
  'CLOSING',
  'TRACKING_LOST',
]);

/** Source channel of an FXMate position. */
export const ChannelRefSchema = z.object({
  id: ChannelIdSchema,
  title: z.string().min(1).max(256),
});

/**
 * Position warnings (FXM-04 §6 case 11): the API sends only `STOP_LOSS_REFUSED_BY_BROKER` today
 * (producer view). Any code of the right shape is still accepted so a warning added later does not
 * break the list (R10); unknown ones show the generic C2 text. Whether warnings should later carry
 * other FXM-04 §6 codes is open (C1 §8, question for Sofien).
 */
export const PositionWarningCodeSchema = openReasonCode(['STOP_LOSS_REFUSED_BY_BROKER']);

/**
 * Where the stop loss stands for the risk at stop loss (specs/P10-risk-at-stop/spec.md R2–R4;
 * Proposal, to be validated, Q259): `SET` a stop loss on the losing side of the entry, so
 * `riskAtStop` is the loss if it is hit (null when the API cannot compute it); `PROTECTED` a stop
 * loss at or beyond the entry in the trader's favour (break even done), risk 0; `NONE` no stop
 * loss, `riskAtStop` null. Closed on purpose, like every success enum (C1 R16).
 */
export const StopStateSchema = z.enum(['SET', 'PROTECTED', 'NONE']);
/** Stop state. */
export type StopState = z.infer<typeof StopStateSchema>;

const positionBase = {
  id: PositionIdSchema,
  symbol: z.string().min(1).max(32),
  side: TradeSideSchema,
  lots: PositiveDecimalSchema,
  entryPrice: PositiveDecimalSchema.nullable().describe('Obtained price; null while opening.'),
  stopLoss: PositiveDecimalSchema.nullable(),
  takeProfit: PositiveDecimalSchema.nullable(),
  status: OpenPositionStatusSchema,
  floatingResult: MoneySchema.nullable().describe(
    'Current result in the account currency (Q27); null when unknown.',
  ),
  openedAt: IsoDateTimeSchema.nullable(),
  /**
   * When `floatingResult` was last known (C1 §5.2, decision 7). Non-null only on a
   * `TRACKING_LOST` row whose last result is known; E8 then shows that result with this time
   * (provisional, Q54). On every row: proposal, to be validated. The screen reads it only on
   * `TRACKING_LOST`.
   */
  resultAsOf: IsoDateTimeSchema.nullable(),
  warnings: z.array(PositionWarningCodeSchema),
  stopState: StopStateSchema.describe(
    'SET: stop loss on the losing side of the entry; PROTECTED: at or beyond the entry in the ' +
      "trader's favour (risk 0); NONE: no stop loss (P10 R2-R4, Q259).",
  ),
  riskAtStop: MoneySchema.nullable().describe(
    'Loss if the stop loss is hit now, in the account currency: |entry - SL| x volume x money per ' +
      'price unit (P10 R1). 0 when PROTECTED; null when NONE, or when SET and the tick value or ' +
      'the entry is not known (never 0, P10 R5).',
  ),
};

/** An open position; FXMate positions are kept apart from manual ones (FXM-04 §4). */
export const OpenPositionSchema = z.discriminatedUnion('origin', [
  z.object({
    origin: z.literal('FXMATE'),
    ...positionBase,
    channel: ChannelRefSchema,
    announcedEntryPrice: PositiveDecimalSchema.nullable().describe(
      'Price announced by the channel (FXM-04 §4).',
    ),
  }),
  z.object({ origin: z.literal('MANUAL'), ...positionBase }),
]);
/** Open position. */
export type OpenPosition = z.infer<typeof OpenPositionSchema>;

/** Response of GET /positions, polled every 5 s while visible (master X8). */
export const PositionListResponseSchema = z.object({
  positions: z.array(OpenPositionSchema),
  refreshedAt: IsoDateTimeSchema,
});
/** GET /positions response. */
export type PositionListResponse = z.infer<typeof PositionListResponseSchema>;

/**
 * Notes on a closed position (C1 §5.2 "History", decision 1): reason codes that add to the close
 * reason. The only one today is `PARTIAL_CLOSE_ROUNDED_TO_FULL` (FXM-04 §6 case 12: a partial
 * close below the broker's minimum closed the whole position, I7). Closed on purpose: a note
 * the Mini App has no text for fails the parse, like every success enum (C1 R16).
 */
export const ClosedPositionNoteSchema = ReasonCodeSchema.extract(['PARTIAL_CLOSE_ROUNDED_TO_FULL']);

/** A position closed by FXMate, with its result and reason (FXM-04 §4, §5). */
export const ClosedPositionSchema = z.object({
  id: PositionIdSchema,
  channel: ChannelRefSchema,
  symbol: z.string().min(1).max(32),
  side: TradeSideSchema,
  lots: PositiveDecimalSchema,
  entryPrice: PositiveDecimalSchema,
  closePrice: PositiveDecimalSchema,
  openedAt: IsoDateTimeSchema,
  closedAt: IsoDateTimeSchema,
  result: MoneySchema,
  /**
   * Who or what closed it, as a C2 closing code shown with `t('reason.<code>')` (C1 decision 1,
   * C2 R10). `CLOSED_BY_EMERGENCY_STOP` is provisional (Q71), `CLOSE_REASON_UNKNOWN` too (Q56,
   * Q67, Q99: the real adapter answers it whenever the engine cannot tell).
   */
  closeReason: ClosingReasonSchema,
  notes: z.array(ClosedPositionNoteSchema),
});
/** Closed position. */
export type ClosedPosition = z.infer<typeof ClosedPositionSchema>;

/** Query of GET /history: opaque cursor and page size 1 to 100. Proposal, to be validated. */
export const HistoryQuerySchema = z.strictObject({
  cursor: z.string().min(1).max(256).optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(100)
    .optional()
    .describe('The API picks the default page size.'),
});
/** History query. */
export type HistoryQuery = z.infer<typeof HistoryQuerySchema>;

/** Response of GET /history, newest first. */
export const HistoryResponseSchema = z.object({
  positions: z.array(ClosedPositionSchema),
  nextCursor: z.string().nullable(),
});
/** GET /history response. */
export type HistoryResponse = z.infer<typeof HistoryResponseSchema>;

/** Path parameters of GET /positions/{positionId}/timeline. */
export const PositionPathParamsSchema = z.strictObject({ positionId: PositionIdSchema });

/**
 * Interpreted action, the FXM-03 §4 categories (C1 §5.2, provisional until Q13; replaces the
 * single `MODIFY`). An action added by a newer API reads as `'UNKNOWN'` (R10), shown as a
 * neutral line.
 */
export const InstructionActionSchema = openEnum([
  'OPEN',
  'MOVE_SL_TO_BREAK_EVEN',
  'MODIFY_LEVELS',
  'REMOVE_LEVELS',
  'PARTIAL_CLOSE',
  'CLOSE',
  'CLOSE_ALL',
  'CANCEL',
  'IGNORED_AMBIGUOUS',
]);

/**
 * Execution outcome (C1 §5.2): `NOT_APPLICABLE` comes with its `reasonCode` (e.g. `CHANNEL_PAUSED`,
 * `DAILY_LOSS_LIMIT_REACHED`, `SIGNAL_WITHOUT_STOP_LOSS`); `PENDING` is awaiting the trader's
 * validation, shown with no action (provisional, Q25). A newer one reads as `'UNKNOWN'` (R10).
 */
export const ExecutionOutcomeSchema = openEnum(['EXECUTED', 'FAILED', 'NOT_APPLICABLE', 'PENDING']);

/** Why an execution failed: any C2 code may appear here, so the producer view is the whole catalogue. */
const ExecutionReasonCodeSchema = openReasonCode();

/** One timeline entry: what the channel said, what FXMate understood, what was executed (FXM-03 §4, I10). */
export const TimelineEntrySchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('CHANNEL_MESSAGE'),
    at: IsoDateTimeSchema,
    messageId: z.string().min(1).max(64),
    revision: z.int().min(0).describe('0 for the original post, then one per edit.'),
    text: z.string().max(4096),
    deleted: z
      .boolean()
      .describe('The channel deleted the message; the position is not closed for it (FXM-03 §4).'),
    replyToMessageId: z
      .string()
      .min(1)
      .max(64)
      .nullable()
      .describe('The message this one replies to in the channel, if any.'),
  }),
  z.object({
    kind: z.literal('INTERPRETATION'),
    at: IsoDateTimeSchema,
    messageId: z.string().min(1).max(64),
    revision: z.int().min(0),
    action: InstructionActionSchema,
    symbol: z.string().min(1).max(32).nullable(),
    side: TradeSideSchema.nullable(),
    entryPrice: PositiveDecimalSchema.nullable(),
    stopLoss: PositiveDecimalSchema.nullable(),
    takeProfit: PositiveDecimalSchema.nullable(),
    closePercent: PositiveDecimalSchema.nullable(),
  }),
  z.object({
    kind: z.literal('EXECUTION'),
    at: IsoDateTimeSchema,
    outcome: ExecutionOutcomeSchema,
    reasonCode: ExecutionReasonCodeSchema.nullable(),
  }),
]);
/** Timeline entry. */
export type TimelineEntry = z.infer<typeof TimelineEntrySchema>;

/**
 * Response of GET /positions/{positionId}/timeline. `available: false` with `entries: []` is a
 * 200, never an error: the message → position link is not known for this position (Q34), shown
 * with the `TIMELINE_UNAVAILABLE` text (C1 §5.2, decision 7).
 */
export const PositionTimelineResponseSchema = z.object({
  positionId: PositionIdSchema,
  available: z.boolean(),
  entries: z.array(TimelineEntrySchema),
});
/** Timeline response. */
export type PositionTimelineResponse = z.infer<typeof PositionTimelineResponseSchema>;

const ENGINE =
  'positions come from MongoDB and the MT5 server; the message → position link is missing (RFX analysis §8).';

/** GET /positions. */
export const listPositionsRoute = defineRoute({
  operationId: 'listPositions',
  method: 'GET',
  path: '/positions',
  tag: 'Positions',
  summary: 'List open positions',
  description: 'FXMate and manual positions, kept apart, with announced and obtained prices.',
  source: 'technical spec §6; FXM-04 §4, §5, §6 case 11; US-15, US-17',
  auth: 'bearer',
  provisional: { questions: ['Q27', 'Q29', 'Q34', 'Q54'], note: ENGINE },
  success: {
    kind: 'json',
    status: 200,
    body: { name: 'PositionListResponse', schema: PositionListResponseSchema },
  },
});

/** GET /history. */
export const listHistoryRoute = defineRoute({
  operationId: 'listHistory',
  method: 'GET',
  path: '/history',
  tag: 'Positions',
  summary: 'List closed positions',
  description: 'Closed FXMate positions with result and close reason; no statistics (FXM-04 §8).',
  source: 'technical spec §6; FXM-04 §4, §5; US-16',
  auth: 'bearer',
  provisional: { questions: ['Q34', 'Q56', 'Q67', 'Q71', 'Q99'], note: ENGINE },
  query: HistoryQuerySchema,
  success: {
    kind: 'json',
    status: 200,
    body: { name: 'HistoryResponse', schema: HistoryResponseSchema },
  },
});

/** GET /positions/{positionId}/timeline. Works for open and closed FXMate positions. */
export const getPositionTimelineRoute = defineRoute({
  operationId: 'getPositionTimeline',
  method: 'GET',
  path: '/positions/{positionId}/timeline',
  tag: 'Positions',
  summary: 'Get what the channel said and what FXMate understood',
  description:
    'Channel messages, revisions and interpretations of a position, side by side (FXM-03 §4, I10).',
  source: 'technical spec §6; FXM-03 §4; US-15',
  auth: 'bearer',
  provisional: { questions: ['Q13', 'Q25', 'Q34'], note: ENGINE },
  params: PositionPathParamsSchema,
  success: {
    kind: 'json',
    status: 200,
    body: { name: 'PositionTimelineResponse', schema: PositionTimelineResponseSchema },
  },
});
